import { contactPhoneSchema, type FounderDetail } from '@mfos/shared';
import { type SubmitEvent, useState } from 'react';
import { Link, useParams } from 'react-router';
import { AccessDenied } from '../../components/AccessDenied';
import { StatusBadge } from '../../components/Badge';
import { ErrorNotice, LoadingNotice, SuccessNotice } from '../../components/ErrorNotice';
import { TextField } from '../../components/Field';
import {
  formatDate,
  formatDateTime,
  formatPercent,
  formatShareCount,
  humanize,
} from '../../lib/format';
import { blankToNull } from '../../lib/validation';
import { useUserTimeZone } from '../auth/auth.api';
import { ApprovalSource } from '../equity/EquityPositionDetails';
import { useObligations } from '../obligations/obligations.api';
import { ObligationList } from '../obligations/ObligationList';
import { ObligationPeriodsSection } from '../obligations/ObligationPeriodsSection';
import { ContributionsSection } from '../reports/ContributionsSection';
import { can, canReadObligations, useMembership } from '../workspace/membership-context';
import { useFounder, useUpdateFounder } from './founders.api';
import { phrase } from '../../lib/i18n/runtime';

export function FounderDetailPage() {
  const membership = useMembership();
  const { founderId = '' } = useParams();
  const allowed = can(membership, 'founders.read') || membership.founderProfileId === founderId;
  const founder = useFounder(membership.workspace.id, founderId, allowed);

  if (!allowed) {
    return <AccessDenied />;
  }
  if (founder.isPending) {
    return <LoadingNotice>{phrase('Loading founder…')}</LoadingNotice>;
  }
  if (founder.isError) {
    return <ErrorNotice error={founder.error} title={phrase('Founder could not be loaded')} />;
  }
  return <FounderDetailView founder={founder.data} />;
}

function FounderDetailView({ founder }: { founder: FounderDetail }) {
  const membership = useMembership();
  const timeZone = useUserTimeZone();
  const isSelf = membership.founderProfileId === founder.id;
  const showObligations = canReadObligations(membership, founder.id);
  const obligations = useObligations(membership.workspace.id, founder.id, showObligations);
  const canEditPhone = isSelf || can(membership, 'founders.manage');

  return (
    <div className="stack stack--lg">
      <div>
        <p className="meta">
          <Link to={`/w/${membership.workspace.id}/founders`}>{phrase('Founders')}</Link> /
        </p>
        <h1>{founder.displayName}</h1>
        <p className="lead">
          {humanize(founder.role)}
          {founder.title !== null ? ` · ${founder.title}` : ''}
        </p>
      </div>

      <section className="card" aria-labelledby="profile-heading">
        <h2 id="profile-heading">{phrase('Profile')}</h2>
        <dl className="summary-list">
          {founder.legalName !== null && (
            <div>
              <dt>{phrase('Legal name')}</dt>
              <dd>{founder.legalName}</dd>
            </div>
          )}
          <div>
            <dt>{phrase('Status')}</dt>
            <dd>
              <StatusBadge status={founder.status} />
            </dd>
          </div>
          <div>
            <dt>{phrase('Founder since')}</dt>
            <dd>
              {founder.founderSince !== null ? formatDate(founder.founderSince) : phrase('Not set')}
            </dd>
          </div>
          {founder.departedOn !== null && (
            <div>
              <dt>{phrase('Departed on')}</dt>
              <dd>
                {formatDate(founder.departedOn)}
                {founder.departureReason !== null ? ` — ${founder.departureReason}` : ''}
              </dd>
            </div>
          )}
          {(founder.contactPhone !== null || canEditPhone) && (
            <div>
              <dt>{phrase('Contact phone')}</dt>
              <dd>{founder.contactPhone ?? phrase('Not set')}</dd>
            </div>
          )}
        </dl>
        {canEditPhone && <ContactPhoneForm founder={founder} />}
      </section>

      <section className="card" aria-labelledby="equity-history-heading">
        <h2 id="equity-history-heading">{phrase('Equity history')}</h2>
        {founder.equityHistory.length === 0 ? (
          <p className="meta">{phrase('No equity history is visible to you.')}</p>
        ) : (
          <div
            className="table-scroll"
            role="region"
            aria-label={phrase('Equity history')}
            tabIndex={0}
          >
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{phrase('Version')}</th>
                  <th scope="col">{phrase('Status')}</th>
                  <th scope="col">{phrase('Effective from')}</th>
                  <th scope="col">{phrase('Ownership')}</th>
                  <th scope="col">{phrase('Shares')}</th>
                  <th scope="col">{phrase('Reason')}</th>
                  <th scope="col">{phrase('Approval')}</th>
                </tr>
              </thead>
              <tbody>
                {founder.equityHistory.map((item) => (
                  <tr key={item.capTableVersionId}>
                    <th scope="row">#{item.versionNumber}</th>
                    <td>
                      <StatusBadge status={item.status} />
                    </td>
                    <td>{formatDate(item.effectiveFrom)}</td>
                    <td className="numeric">
                      {item.percentage !== null
                        ? formatPercent(item.percentage)
                        : phrase('Not included')}
                    </td>
                    <td className="numeric">
                      {item.shareCount !== null ? formatShareCount(item.shareCount) : '—'}
                    </td>
                    <td>{item.reason}</td>
                    <td>
                      <ApprovalSource
                        approvalDecisionId={item.approvalDecisionId}
                        approvalReference={item.approvalReference}
                      />
                      {item.approvedAt !== null && (
                        <span className="meta"> · {formatDateTime(item.approvedAt, timeZone)}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showObligations && (
        <section className="card" aria-labelledby="obligations-heading">
          <h2 id="obligations-heading">{phrase('Responsibilities')}</h2>
          {obligations.isPending && (
            <LoadingNotice>{phrase('Loading responsibilities…')}</LoadingNotice>
          )}
          {obligations.isError && <ErrorNotice error={obligations.error} />}
          {obligations.isSuccess && (
            <ObligationList
              obligations={obligations.data.items}
              timeZone={obligations.data.timezone}
            />
          )}
        </section>
      )}

      {showObligations && (
        <ObligationPeriodsSection
          workspaceId={membership.workspace.id}
          founderId={founder.id}
          timeZone={timeZone}
        />
      )}

      {can(membership, 'reports.read') && (
        <ContributionsSection
          workspaceId={membership.workspace.id}
          founderId={founder.id}
          timeZone={timeZone}
        />
      )}
    </div>
  );
}

function ContactPhoneForm({ founder }: { founder: FounderDetail }) {
  const membership = useMembership();
  const [phone, setPhone] = useState(founder.contactPhone ?? '');
  const [phoneError, setPhoneError] = useState<string | undefined>();
  const mutation = useUpdateFounder(membership.workspace.id);

  function submit(event: SubmitEvent) {
    event.preventDefault();
    const value = blankToNull(phone);
    if (value !== null && !contactPhoneSchema.safeParse(value).success) {
      setPhoneError('Use digits, spaces, (), - and an optional leading +.');
      return;
    }
    setPhoneError(undefined);
    mutation.mutate({
      founderId: founder.id,
      body: { version: founder.version, contactPhone: value },
    });
  }

  return (
    <form onSubmit={submit} noValidate className="inline-form">
      <TextField
        label={phrase('Update contact phone')}
        type="tel"
        autoComplete="tel"
        value={phone}
        onValueChange={setPhone}
        error={phoneError}
        hint={phrase('Leave empty to remove it.')}
      />
      <button type="submit" className="button button--secondary" disabled={mutation.isPending}>
        {mutation.isPending ? phrase('Saving…') : phrase('Save phone')}
      </button>
      <ErrorNotice error={mutation.error} />
      {mutation.isSuccess && <SuccessNotice>{phrase('Contact phone saved.')}</SuccessNotice>}
    </form>
  );
}
